import _ from 'lodash'
import getSlug from 'speakingurl'
import Vue from 'vue'
import i18n from '_/i18n'
import { userHasRole } from '_/vuex/actions/userActions'
import { eventBus } from '_/helpers/eventBus'
import { getObjectPropertyValueByPath } from '_/helpers/lodashHelper'
import { router } from '_/router/router'
import { storage } from '_/services/storage'
import { mapActions, mapGetters } from 'vuex'
import { store } from '_/vuex/store'
import { api } from '_/services/api'
import { pError } from '_/helpers/logger'

const InputTextField = Vue.component(
  'InputTextField',
  require('_/components/FieldTypes/InputTag/InputTag.vue').default
)

const ActionButton = Vue.component(
  'ActionButton',
  require('_/components/UserInterface/ActionButton/ActionButton.vue').default
)

const InvitationsButtons = Vue.component(
  'InvitationsButtons',
  require('_/components/UserInterface/EntryForm/InvitationsButtons/InvitationsButtons.vue')
    .default
)

const ImpersonateButton = Vue.component(
  'ImpersonateButton',
  require('_/components/UserInterface/EntryForm/ImpersonateButton/ImpersonateButton.vue')
    .default
)

export default {
  /**
   * Define props
   *
   * @type  {Object}
   */
  props: {
    vuexModule: {
      required: true,
      type: String,
    },
    disableDelete: {
      default: false,
      type: Boolean,
    },
    shouldRouterPush: {
      default: true,
      type: Boolean,
    },
    disableCreateAnother: {
      default: false,
      type: Boolean,
    },
  },

  /**
   * Set the data property
   *
   * @return  {object}
   */
  data() {
    return {
      filteredLocales: [],
      isAlreadyPublished: false,
      publishText: 'Publish',
      unpublishText: 'Unpublish',
      schedulePublishText: 'Schedule publish',
      schedulePublishDateText: 'Published at:',
    }
  },

  /**
   * Set the components
   *
   * @type  {Object}
   */
  components: {
    InputTextField,
    InvitationsButtons,
    ImpersonateButton,
  },

  /**
   * Set the computed properties
   *
   * @type {object}
   */
  computed: {
    ...mapGetters({
      photonModule: 'photonModule/photonModule',
      admin: 'admin',
      subscription: 'subscription/subscription',
      ui: 'ui/ui',
      user: 'user/user',
    }),

    /**
     * Gets the scheduled publishing modal name
     *
     * @return  {string}
     */
    schedulePublishingModalName() {
      return 'schedule-publishing-' + this.vuexModule
    },

    getLocales() {
      return this.ui.locales.map(locale => {
        return {
          id: locale.languageCode,
          text: locale.name,
        }
      })
    },

    dataOverdraft() {
      let selectedModuleTableName = this.admin.selectedModule.table_name
      let selectedModuleId = this.photonModule.moduleTableNameToIdMap[
        selectedModuleTableName
      ]
      let isOverdraft = this.photonModule.moduleIdToModuleObjectMap[
        selectedModuleId
      ].overdraft_status
      return isOverdraft
    },

    /**
     * Create the admin getter based on the vuexModule prop
     *
     * @return  {object}
     */
    admin() {
      const getterName = `${this.vuexModule}/${this.vuexModule}`

      return this.$store.getters[getterName]
    },

    /**
     * Sets the action button text
     *
     * @returns {VueI18n.TranslateResult}
     */
    newEntryButtonText() {
      if (
        this.vuexModule === 'admin' &&
        this[this.vuexModule].selectedModule.table_name === 'invitations'
      ) {
        return this.$t('admin.sendInvitation')
      }

      if (this.admin.selectedModule.is_system) {
        return this.$t('admin.create')
      }

      return this.$t('admin.saveAsDraft')
    },

    /**
     * Checkbox two-way binding model
     *
     * @type {object}
     */
    createAnother: {
      get() {
        return this.admin.createAnother
      },
      set(value) {
        this.createAnotherEntry(value)
      },
    },

    /**
     * Date format getter
     *
     * @return  {string}
     */
    dateFormat() {
      return this.ui.photonConfig.dateFormat
    },

    /**
     * Created at getter
     *
     * @return  {string}
     */
    createdAt() {
      return _.get(this.admin.editedEntry, 'system.created_at')
        ? moment(_.get(this.admin.editedEntry, 'system.created_at')).format(
            this.dateFormat
          )
        : null
    },

    /**
     * Updated at getter
     *
     * @return  {string}
     */
    updatedAt() {
      return _.get(this.admin.editedEntry, 'system.updated_at')
        ? moment(_.get(this.admin.editedEntry, 'system.updated_at')).format(
            this.dateFormat
          )
        : null
    },

    /**
     * Published at getter
     *
     * @returns {string}
     */
    publishedAt() {
      return _.get(this.admin.editedEntry, 'system.published_at')
        ? moment(_.get(this.admin.editedEntry, 'system.published_at')).format(
            this.dateFormat
          )
        : null
    },

    /**
     * Module type getter
     *
     * @return  {string}
     */
    moduleType: function() {
      return this.ui.photonConfig.moduleTypes[
        store.state.admin.selectedModule.type
      ]
        ? this.ui.photonConfig.moduleTypes[
            store.state.admin.selectedModule.type
          ]
        : 'System module (Type not set)'
    },

    /**
     * Parent entry getter
     *
     * @return  {string}
     */
    parentEntry: function() {
      if (this.admin.editorMode === 'create') {
        return !_.isEmpty(this.admin.selectedNode)
          ? this.admin.selectedNode
          : null
      }

      return null
    },

    /**
     * Checks if the entry is a root prent entry
     *
     * @return  {Boolean}
     */
    isRootParentEntry: function() {
      if (this.admin.selectedNode.url === null) {
        return true
      }

      return false
    },

    /**
     * Returns the parent module
     *
     * @return  {object}
     */
    parentModule: function() {
      if (this.admin.selectedModule.category) {
        const parentModule = _.find(this.photonModule.modules, {
          id: this.admin.selectedModule.category,
        })

        return parentModule
      }

      return null
    },

    /**
     * Returns slug error, if exists
     *
     * @return  {string}
     */
    slugError: function() {
      return _.has(this.admin.error.fields, 'slug')
        ? this.admin.error.fields.slug[0]
        : null
    },
  },

  /**
   * Set the methods
   *
   * @type  {Object}
   */
  methods: {
    ...mapActions('ui', ['setFilteredLocales', 'triggerCreateMode']),

    ...mapActions('admin', [
      'disableAutomaticSlugGeneration',
      'updateEntryField',
      'toggleParentSelectionMode',
    ]),

    ...mapActions('photonModule', ['getPhotonModules']),

    getObjectPropertyValueByPath,

    schedulePublishing() {
      eventBus.$emit(
        'openSchedulePublishingModal',
        this.schedulePublishingModalName
      )
    },

    /**
     * Checks if the given permission type is approved or not
     *
     * @param   {string}  permissionType
     * @return  {bool}
     */
    checkPermission(permissionType) {
      if (
        !_.has(
          this.admin,
          `editedEntry.system.permission_control.crud.${permissionType}`
        )
      ) {
        return false
      }

      return _.get(
        this.admin,
        `editedEntry.system.permission_control.crud.${permissionType}`
      )
    },

    openPreviewTab() {
      let url = this.admin.entry.system.content_preview
      const newWindow = window.open(url, '_blank')

      if (newWindow) {
        newWindow.focus()
      } else {
        alert('Please allow popups for this website.')
      }
    },

    /**
     * Fired on translations dropdown change event
     *
     * @param   {string}  options.id
     * @param   {array}  options.value
     * @return  {void}
     */
    onLocaleFilterChange({ id, value }) {
      this.setFilteredLocales({ value })
    },

    /**
     * Submit form handler
     *
     * @type {void}
     */
    submit() {
      const $form = $(this.$root.$el).find(
        `#entry-editor-form-${this.vuexModule}`
      )
      $form.submit()
    },

    /**
     * Unpublish entry and save form
     *
     * @type {void}
     */
    unpublish() {
      store.dispatch(`${this.vuexModule}/updateEntrySystemField`, {
        name: 'published_at',
        newValue: null,
      })
      this.submit()
    },

    /**
     * Set published_at datetime for entry to current datetime and save form
     *
     * @type {void}
     */
    publish() {
      store.dispatch(`${this.vuexModule}/updateEntrySystemField`, {
        name: 'published_at',
        newValue: moment().format('YYYY-MM-DD HH:mm:ss'),
      })
      this.submit()
    },

    /**
     * Duplicates the entry
     *
     * @type mixed
     */
    duplicate() {
      store
        .dispatch(`${this.vuexModule}/setSubmitEntryInProgress`, {
          value: true,
        })
        .then(() => {
          let uri =
            'duplicate/' +
            this.admin.selectedModule.table_name +
            '/' +
            this.admin.entry.system.id
          let payload = []
          api
            .post(uri, payload)
            .then(response => {
              if (
                _.has(response, 'body.body.entry.system.id') &&
                response.body.body.entry.system.id !== undefined
              ) {
                return store
                  .dispatch(`${this.vuexModule}/setSubmitEntryInProgress`, {
                    value: false,
                  })
                  .then(() => {
                    return this.$router.push(
                      `/admin/${this.admin.selectedModule.table_name}/${response.body.body.entry.system.id}`
                    )
                  })
              }

              return alert(
                'Error while trying to duplicate the entry:',
                response.message
              )
            })
            .catch(response => {
              pError('Error while trying to duplicate the entry:', response)
            })
        })
    },

    /**
     * Dispatches the confirmDeleteEntry action
     *
     * @return  {void}
     */
    confirmDeleteEntry() {
      return store.dispatch(`${this.vuexModule}/confirmDeleteEntry`)
    },

    /**
     * Prepares the payload object and runs the deleteEntry method
     *
     * {boolean}  options.force
     * @return  {promise}
     */
    deleteEntryConfirmed({ force = false } = {}) {
      eventBus.$emit('deleteEntryConfirmed')
      this.deleteEntry({
        entryId: _.get(this.admin.editedEntry, 'system.id'),
        selectedModuleName: this.admin.selectedModule.table_name,
        force,
      })
    },

    /**
     * Prepares the payload object and runs the deleteEntry method for dropdown button
     *
     * {boolean}  options.force
     * @return  {promise}
     */
    deleteEntryDropdown({ force = false } = {}) {
      let confirmed = confirm(this.$t('admin.confirmDelete'))
      if (confirmed === true) {
        force = true
        store.dispatch(`${this.vuexModule}/confirmDeleteEntry`)
        eventBus.$emit('deleteEntryConfirmed')
      }

      this.deleteEntry({
        entryId: _.get(this.admin.editedEntry, 'system.id'),
        selectedModuleName: this.admin.selectedModule.table_name,
        force,
      })
    },

    /**
     * Commits the deleteEntry action
     *
     * @param   {integer}  options.entryId
     * @param   {string}  options.selectedModuleName
     * {boolean}  options.force*
     * @return  {promise}
     */
    deleteEntry({ entryId, selectedModuleName, force }) {
      store
        .dispatch(`${this.vuexModule}/deleteEntry`, {
          entryId,
          selectedModuleName,
          force,
        })
        .then(() => {
          if (this.shouldRouterPush) {
            router.push(`/admin/${selectedModuleName}`)
          }

          store.dispatch('admin/setAdminUi')
        })
        .catch(() => {
          this.admin.submitInProgress = false
        })
    },

    /**
     * Dispatches the createAnother action
     *
     * @param   {boolean}  value
     * @return  {void}
     */
    createAnotherEntry(value) {
      return store.dispatch(`${this.vuexModule}/createAnother`, {
        value,
      })
    },

    /**
     * Fired on slug input tag change
     *
     * @param   {string}  options.value
     * @return  {void}
     */
    onChange({ value }) {
      store.dispatch(`${this.vuexModule}/updateEntrySystemField`, {
        name: 'slug',
        newValue: value,
      })

      if (
        this.admin.editorMode !== 'create' ||
        this.vuexModule !== 'admin' ||
        this.admin.disableAutomaticSlugGeneration
      ) {
        return false
      }

      const slug = this.slugify(this.admin.editedEntry)

      if (value !== slug) {
        store.dispatch(`${this.vuexModule}/disableAutomaticSlugGeneration`, {
          value: true,
        })
      }
    },

    publishChange({ value }) {
      store.dispatch(`${this.vuexModule}/updateEntryField`, {
        name: 'published_at',
        newValue: value,
      })
    },

    /**
     * Triggers the create mode
     *
     * @return  {void}
     */
    createNewEntry() {
      this.triggerCreateMode()
    },

    /**
     * Creates the slug version of the string template
     *
     * @return  {string}
     * @param editedEntry
     */
    slugify(editedEntry) {
      if (!this.admin.selectedModule.slug) {
        return false
      }

      const languageCode = this.ui.defaultLocale.languageCode
      const selectedModuleSlug = this.admin.selectedModule.slug
      if (!selectedModuleSlug) {
        return
      }
      let fieldNames = selectedModuleSlug.match(/[^{{\}}]+(?=})/g)
      let slug = ''
      fieldNames.forEach(function(fieldName) {
        let value = ''
        fieldName = fieldName.replace('fields.', '')
        if (_.has(editedEntry.fields[fieldName], languageCode)) {
          value = editedEntry.fields[fieldName][languageCode]
        } else if (_.has(editedEntry.fields, fieldName)) {
          value = editedEntry.fields[fieldName]
        } else value = ''
        slug = slug + '-' + value
      })

      return getSlug(slug)
    },

    /**
     * Checks if a user has given role
     *
     * @param   {string}  role
     * @return  {bool}
     */
    userHasRole,
  },

  /**
   * Set the mounted hook
   *
   * @return  {void}
   */
  mounted() {
    this.$nextTick(() => {
      // Used only to send the starting
      this.filteredLocales = this.ui.filteredLocales

      // Use uniform to style checkbox/radio elements
      $(this.$el)
        .find('input[type="checkbox"]')
        .uniform()
    })
  },

  /**
   * Set the watched properties
   *
   * @type  {Object}
   */
  watch: {
    'admin.editedEntry': {
      deep: true,
      handler(newValue) {
        if (
          this.admin.editorMode !== 'create' ||
          this.vuexModule !== 'admin' ||
          this.admin.disableAutomaticSlugGeneration
        ) {
          return false
        }

        let slug = this.slugify(newValue)
        return store.dispatch(`${this.vuexModule}/updateEntrySystemField`, {
          name: 'slug',
          newValue: slug,
        })
      },
    },
    'admin.dirtyFields': {
      deep: true,
      handler(newValue) {
        this.unpublishText = this.$t('admin.unpublish')
        this.publishText = this.$t('admin.publish')
        this.schedulePublishText = this.$t('admin.schedulePublishing')

        let date_time = moment().format('YYYY-MM-DD HH:mm:ss')
        this.isAlreadyPublished = moment(date_time).isSameOrAfter(
          _.get(this.admin.editedEntry, 'system.published_at')
        )

        if (this.isAlreadyPublished) {
          this.schedulePublishDateText = this.$t('admin.publishedAt')
        } else {
          this.schedulePublishDateText = this.$t('admin.scheduledAt')
          if (
            _.has(this.admin.editedEntry, 'system.published_at') &&
            this.admin.editedEntry.system.published_at !== null
          ) {
            this.schedulePublishText = this.$t('admin.reschedulePublishing')
            this.unpublishText = this.$t('admin.unschedule')
          }
        }
        if (newValue.length > 0) {
          this.unpublishText = this.$t('admin.saveAndUnpublish')
          this.publishText = this.$t('admin.saveAndPublish')
          this.schedulePublishText = this.$t('admin.saveAndSchedulePublishing')
          if (
            !this.isAlreadyPublished &&
            _.has(this.admin.editedEntry, 'system.published_at') &&
            this.admin.editedEntry.system.published_at !== null
          ) {
            this.unpublishText = this.$t('admin.saveAndUnschedule')
            this.schedulePublishText = this.$t(
              'admin.saveAndReschedulePublishing'
            )
          }
        }
        return
      },
    },

    /**
     * Watching the route change
     *
     * @return  {void}
     */
    $route(newEntry, oldEntry) {
      // Use uniform to style checkbox/radio elements
      $(this.$el)
        .find('input[type="checkbox"]')
        .uniform()
    },
  },
}
